import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Add, Delete, Save, CloudUpload } from "@mui/icons-material";
import { ComponentHeader, BrandButton } from "components";
import {
  getAllSocialMediaLinks,
  createSocialMediaLink,
  updateSocialMediaLink,
  deleteSocialMediaLink,
} from "actions/socialMediaLinks";
import { clearError } from "features/socialMediaLinksSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import "./ContactUsSocialMedia.scss";

const T = ADMIN_TEXTS.contactUs;
const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const ImageUploadArea = ({ preview, onChange, error }) => {
  const inputRef = useRef(null);

  return (
    <Box>
      <Paper
        variant="outlined"
        onClick={() => inputRef.current?.click()}
        className={`upload-area${error ? " upload-area--error" : ""}`}
      >
        {preview ? (
          <img
            src={preview}
            alt="icon preview"
            className="upload-area__preview"
          />
        ) : (
          <>
            <CloudUpload className="upload-area__icon" />
            <Typography variant="caption" className="upload-area__text">
              {T.uploadIconHint}
            </Typography>
          </>
        )}
      </Paper>
      {error && (
        <Typography
          variant="caption"
          color="error"
          className="upload-area__error"
        >
          {error}
        </Typography>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onChange}
      />
    </Box>
  );
};

const EMPTY_DIALOG = { name: "", iconFile: null, iconPreview: "", url: "" };
const EMPTY_ERRORS = { name: "", icon: "", url: "" };

const SocialMediaItem = ({ item, onSave, onDelete, saving, deleting }) => {
  const [name, setName] = useState(item.name || "");
  const [url, setUrl] = useState(item.url || "");
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(
    item.icon ? `${imageBase}${item.icon.path}` : "",
  );

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  useEffect(() => {
    setName(item.name || "");
    setUrl(item.url || "");
    setIconFile(null);
    setIconPreview(item.icon ? `${imageBase}${item.icon.path}` : "");
  }, [item]);

  useEffect(() => {
    return () => {
      if (iconPreview.startsWith("blob:")) {
        URL.revokeObjectURL(iconPreview);
      }
    };
  }, [iconPreview]);

  return (
    <Paper variant="outlined" className="contact-us-social-media__item-card">
      <Box className="contact-us-social-media__item-body">
        <Box className="contact-us-social-media__item-icon-wrapper">
          <Tooltip title={T.changeIconTooltip}>
            <Box
              component="label"
              className="contact-us-social-media__item-icon-label"
            >
              {iconPreview ? (
                <img
                  src={iconPreview}
                  alt="icon"
                  className="contact-us-social-media__item-icon"
                />
              ) : (
                <CloudUpload className="contact-us-social-media__item-icon-placeholder" />
              )}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleIconChange}
              />
            </Box>
          </Tooltip>
        </Box>

        <Box className="contact-us-social-media__item-fields">
          <TextField
            fullWidth
            size="small"
            label={T.nameLabel}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            fullWidth
            size="small"
            label={T.urlLabel}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Box>
      </Box>

      <Box className="contact-us-social-media__item-actions">
        <IconButton
          size="small"
          onClick={() => onDelete(item.id)}
          disabled={deleting}
          className="contact-us-social-media__delete-btn"
        >
          {deleting ? (
            <CircularProgress size={16} />
          ) : (
            <Delete fontSize="small" />
          )}
        </IconButton>
        <BrandButton
          variant="primary"
          size="small"
          startIcon={saving ? null : <Save />}
          onClick={() => onSave(item.id, { name, url, iconFile })}
          disabled={saving}
          className="contact-us-social-media__save-btn"
        >
          {saving ? <CircularProgress size={14} /> : T.save}
        </BrandButton>
      </Box>
    </Paper>
  );
};

export const ContactUsSocialMedia = () => {
  const dispatch = useDispatch();
  const { items, loading, createLoading, savingId, deletingId, error } =
    useSelector((state) => state.socialMediaLinks);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogValues, setDialogValues] = useState(EMPTY_DIALOG);
  const [dialogErrors, setDialogErrors] = useState(EMPTY_ERRORS);

  const validateDialog = () => {
    const errors = { name: "", icon: "", url: "" };
    if (!dialogValues.name.trim()) errors.name = T.nameRequired;
    if (!dialogValues.iconFile) errors.icon = T.iconRequired;
    if (!dialogValues.url.trim()) errors.url = T.urlRequired;
    else if (!/^https?:\/\/.+/.test(dialogValues.url.trim()))
      errors.url = T.urlInvalid;
    setDialogErrors(errors);
    return !errors.name && !errors.icon && !errors.url;
  };

  const handleDialogAdd = () => {
    if (!validateDialog()) return;

    const formData = new FormData();
    formData.append("name", dialogValues.name.trim());
    formData.append("url", dialogValues.url.trim());
    formData.append("icon", dialogValues.iconFile);

    dispatch(createSocialMediaLink(formData)).then((result) => {
      if (!result.error) {
        if (dialogValues.iconPreview.startsWith("blob:")) {
          URL.revokeObjectURL(dialogValues.iconPreview);
        }
        setDialogOpen(false);
        setDialogValues(EMPTY_DIALOG);
        setDialogErrors(EMPTY_ERRORS);
      }
    });
  };

  const handleDialogClose = () => {
    if (dialogValues.iconPreview.startsWith("blob:")) {
      URL.revokeObjectURL(dialogValues.iconPreview);
    }
    setDialogOpen(false);
    setDialogValues(EMPTY_DIALOG);
    setDialogErrors(EMPTY_ERRORS);
  };

  const handleItemSave = (id, { name, url, iconFile }) => {
    const formData = new FormData();
    if (name !== undefined) formData.append("name", name);
    if (url !== undefined) formData.append("url", url);
    if (iconFile) formData.append("icon", iconFile);

    dispatch(updateSocialMediaLink({ id, formData }));
  };

  const handleItemDelete = (id) => {
    dispatch(deleteSocialMediaLink(id));
  };

  useEffect(() => {
    dispatch(getAllSocialMediaLinks());
    return () => {
      dispatch(clearError());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <Box className="contact-us-social-media__loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="contact-us-social-media">
      <Box className="contact-us-social-media__header">
        <ComponentHeader title={T.socialMediaPageTitle} />
        <BrandButton
          variant="primary"
          startIcon={<Add />}
          onClick={() => setDialogOpen(true)}
        >
          {T.addBtn}
        </BrandButton>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="contact-us-social-media__alert"
          onClose={() => dispatch(clearError())}
        >
          {typeof error === "string" ? error : T.genericError}
        </Alert>
      )}

      {items.length === 0 ? (
        <Paper
          variant="outlined"
          className="contact-us-social-media__empty-state"
        >
          <Typography
            variant="body1"
            color="text.secondary"
            className="contact-us-social-media__empty-title"
          >
            {T.noLinks}
          </Typography>
          <Typography variant="body2" color="text.disabled">
            {T.noLinksHint}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <SocialMediaItem
                item={item}
                onSave={handleItemSave}
                onDelete={handleItemDelete}
                saving={savingId === item.id}
                deleting={deletingId === item.id}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{T.addDialogTitle}</DialogTitle>
        <DialogContent>
          <Box className="contact-us-social-media__dialog-content">
            <TextField
              fullWidth
              label={T.nameLabel}
              required
              value={dialogValues.name}
              onChange={(e) => {
                setDialogValues((prev) => ({ ...prev, name: e.target.value }));
                setDialogErrors((prev) => ({ ...prev, name: "" }));
              }}
              error={Boolean(dialogErrors.name)}
              helperText={dialogErrors.name || T.nameHelperText}
            />
            <Box>
              <Typography
                variant="body2"
                className="contact-us-social-media__dialog-icon-label"
              >
                {T.iconImageLabel}{" "}
                <span className="contact-us-social-media__required">*</span>
              </Typography>
              <ImageUploadArea
                preview={dialogValues.iconPreview}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (dialogValues.iconPreview.startsWith("blob:")) {
                    URL.revokeObjectURL(dialogValues.iconPreview);
                  }
                  setDialogValues((prev) => ({
                    ...prev,
                    iconFile: file,
                    iconPreview: URL.createObjectURL(file),
                  }));
                  setDialogErrors((prev) => ({ ...prev, icon: "" }));
                }}
                error={dialogErrors.icon}
              />
            </Box>
            <TextField
              fullWidth
              label={T.urlLabel}
              required
              value={dialogValues.url}
              onChange={(e) => {
                setDialogValues((prev) => ({ ...prev, url: e.target.value }));
                setDialogErrors((prev) => ({ ...prev, url: "" }));
              }}
              error={Boolean(dialogErrors.url)}
              helperText={dialogErrors.url || T.urlHelperText}
            />
          </Box>
        </DialogContent>
        <DialogActions className="contact-us-social-media__dialog-actions">
          <BrandButton
            variant="secondary"
            onClick={handleDialogClose}
            disabled={createLoading}
          >
            {T.cancel}
          </BrandButton>
          <BrandButton
            variant="primary"
            onClick={handleDialogAdd}
            disabled={createLoading}
          >
            {createLoading ? <CircularProgress size={16} /> : T.addBtn}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
